import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { apiCalls } from "@/api/inspectorPortalQueries";
import { APP_ROUTES } from "@/config/appConfig";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import { maskPhoneNumber } from "./authUtils";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Navigate } from "react-router-dom";

function getErrorStatus(error: unknown) {
  if (typeof error !== "object" || !error) return undefined;

  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }

  if (
    "response" in error &&
    typeof error.response === "object" &&
    error.response &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }

  return undefined;
}

const TEST_OTP_BYPASS_CODE = "123456";
const shouldShowTestOtpBypass = import.meta.env.MODE !== "production";

export default function OtpPage() {
  const { pendingLogin, session, verifyOtp } = useAuthSession();
  const intl = useIntl();
  const [code, setCode] = useState("");
  const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
  const [infoMessageId, setInfoMessageId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const errorId = "otpError";
  const infoId = "otpInfo";

  if (!pendingLogin) {
    return (
      <Navigate
        to={session ? APP_ROUTES.PORTAL.VIEWINGS : APP_ROUTES.AUTH.LOGIN}
        replace
      />
    );
  }

  return (
    <main className="min-h-screen bg-background p-6 md:p-10">
      <div className="mx-auto flex min-h-[80vh] w-full max-w-md items-center justify-center">
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle>
                {intl.formatMessage({ id: "auth.otp.title" })}
              </CardTitle>
              <LanguageSelector />
            </div>
            <CardDescription>
              <span className="block">
                {intl.formatMessage(
                  { id: "auth.otp.description" },
                  { phone: maskPhoneNumber(pendingLogin.phoneNumber) },
                )}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">
                {intl.formatMessage({ id: "auth.otp.codeLabel" })}
              </Label>
              <InputOTP
                id="otp"
                value={code}
                onChange={(value) => {
                  setCode(value);
                  setErrorMessageId(null);
                }}
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-invalid={errorMessageId ? "true" : undefined}
                aria-describedby={
                  errorMessageId ? errorId : infoMessageId ? infoId : undefined
                }
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <InputOTPSlot key={index} index={index} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {shouldShowTestOtpBypass ? (
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage(
                    { id: "auth.otp.testBypass" },
                    { code: TEST_OTP_BYPASS_CODE },
                  )}
                </p>
              ) : null}
            </div>
            {errorMessageId ? (
              <p id={errorId} role="alert" className="text-sm text-destructive">
                {intl.formatMessage({ id: errorMessageId })}
              </p>
            ) : null}
            {infoMessageId ? (
              <p id={infoId} className="text-sm text-muted-foreground">
                {intl.formatMessage({ id: infoMessageId })}
              </p>
            ) : null}
            <Button
              className="w-full"
              type="button"
              disabled={code.length !== 6 || isSubmitting}
              onClick={async () => {
                try {
                  setIsSubmitting(true);
                  const ok = await verifyOtp(code);
                  if (!ok) {
                    setErrorMessageId("auth.otp.invalid");
                    setInfoMessageId(null);
                  }
                } catch (error) {
                  const status = getErrorStatus(error);
                  setErrorMessageId("auth.otp.invalid");
                  if (status === 429) {
                    setErrorMessageId("auth.errors.throttled");
                  }
                  setInfoMessageId(null);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {intl.formatMessage({
                id: isSubmitting ? "auth.otp.submitting" : "auth.otp.submit",
              })}
            </Button>
            <Button
              className="w-full"
              type="button"
              variant="outline"
              disabled={isResending}
              onClick={async () => {
                try {
                  setIsResending(true);
                  setErrorMessageId(null);
                  await apiCalls.resendInspectorPortalOtp(
                    pendingLogin.phoneNumber,
                  );
                  setInfoMessageId("auth.otp.resent");
                } catch (error) {
                  const status = getErrorStatus(error);
                  setInfoMessageId(null);
                  setErrorMessageId(
                    status === 429
                      ? "auth.errors.throttled"
                      : "auth.errors.generic",
                  );
                } finally {
                  setIsResending(false);
                }
              }}
            >
              {intl.formatMessage({
                id: isResending ? "auth.otp.resending" : "auth.otp.resend",
              })}
            </Button>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
